import React, { useEffect, useState } from 'react';
import {
  checkIfFeatureIsEnabled,
  productFeature,
} from 'lib/utils/productFeature';
import {
  ActionTypes,
  IntegrationDetailsPage,
} from '../details/IntegrationDetailsPage';
import { Zoom } from '../Zoom';
import { Salesforce } from '../Salesforce';
import { useAuth } from 'lib/context';
import {
  checkIfDealerSocketAccessible,
  checkIfElementAccessible,
} from 'lib/utils/automotiveRolePermissionChecks';
import {
  ANDROID_INSTALL_LINK,
  GMAIL_INSTALL_LINK,
  IntegrationsData,
  IOS_URL,
  LINKEDIN_INSTALL_LINK,
  OFFICE_365_HELP_DESK_LINK,
} from 'lib/api/integrations/useGetAllIntegrations';
import { usePodiumOAuthUrlMutation } from 'lib/api/podium/usePodiumOAuthUrlMutation';
import { usePodiumDisconnectMutation } from 'lib/api/podium/usePodiumDisconnectMutation';
import {
  Integration,
  INTEGRATION_BC_CHANNELS,
  INTEGRATION_BC_TYPES,
  INTEGRATION_TYPE,
  IntegrationProps,
} from 'lib/api/integrations/types';

export const useIntegrationData = () => {
  const { userData, whitelabel } = useAuth();
  const [integrations, setIntegrations] = useState<IntegrationProps[]>([]);

  useEffect(() => {
    const bc = new BroadcastChannel(INTEGRATION_BC_CHANNELS.PODIUM);

    const handleMessage = ({ data }: MessageEvent) => {
      if (!data) {
        return;
      }

      const { type, origin } = data;
      if (
        type !== INTEGRATION_BC_TYPES.PODIUM ||
        origin !== window.location.origin
      ) {
        return;
      }

      window.location.reload();
    };

    bc.addEventListener('message', handleMessage);

    return () => {
      bc.removeEventListener('message', handleMessage);
      bc.close();
    };
  }, []);

  /* SUS-1234 changes integrations/dealersocket route are accessible to CDS account with SERVICE_MANAGER, SERVICE_ADVISOR, SALES_MANAGER, TECHNICIAN, SALESPERSON role only and Supervisor CDS user  */
  const isDealerSocketAccessible = checkIfDealerSocketAccessible(userData);

  /* SUS-1234 changes integrations/element451 route are accessible to Supervisor CDS user, Legacy User and standard user with no CDS  */
  const isElementAccessible = checkIfElementAccessible(userData);

  /******/
  const isSalesforceFeatureEnabled = checkIfFeatureIsEnabled(
    userData,
    productFeature.SALESFORCE_INTEGRATION_BASIC
  );

  const {
    mutateAsync: generatePodiumOAuthUrl,
    isLoading: isGeneratingOAuthUrl,
  } = usePodiumOAuthUrlMutation();
  const { mutateAsync: disconnectPodium, isLoading: isDisconnectingPodium } =
    usePodiumDisconnectMutation();

  // get integration data
  const {
    integrations: INTEGRATION,
    filters: DISCOVER_BY_FILTERS,
    cdsCategories: CDS_CATEGORIES,
    categories: INTEGRATIONS_CATEGORIES,
    integrationDetails,
  } = IntegrationsData;

  const isPodiumConnected = !!parseInt(
    userData.isPodiumConnected?.toString() || '0',
    10
  );
  const handlePodiumButtonClick = async () => {
    if (isPodiumConnected) {
      await disconnectPodium();
      window.location.reload();
      return;
    }
    await generatePodiumOAuthUrl();
  };

  // create component mapping
  const IntegrationComponentMapping: object = {
    [INTEGRATION.GMAIL]: {
      component: () => (
        <IntegrationDetailsPage
          actionType={ActionTypes.INSTALL}
          onClick={() => {
            window.open(GMAIL_INSTALL_LINK, '"_new"');
          }}
        />
      ),
      onClick: () => {
        window.open(GMAIL_INSTALL_LINK, '"_new"');
      },
    },
    [INTEGRATION.LINKEDIN]: {
      component: () => (
        <IntegrationDetailsPage
          actionType={ActionTypes.INSTALL}
          connectSocialProfile
          onClick={() => {
            window.open(LINKEDIN_INSTALL_LINK, '"_new"');
          }}
        />
      ),
      onClick: () => {
        window.open(LINKEDIN_INSTALL_LINK, '"_new"');
      },
    },
    [INTEGRATION.OFFICE_365]: {
      component: () => (
        <IntegrationDetailsPage
          actionType={ActionTypes.INSTALL}
          onClick={() => {
            window.open(OFFICE_365_HELP_DESK_LINK, '_blank', 'noopener');
          }}
        />
      ),
      onClick: () => {
        window.open(OFFICE_365_HELP_DESK_LINK, '_blank', 'noopener');
      },
    },
    [INTEGRATION.OUTLOOK]: {
      component: () => (
        <IntegrationDetailsPage
          actionType={ActionTypes.DOWNLOAD}
          onClick={() => {
            window.open(whitelabel.outlookURL, '"_new"');
          }}
        />
      ),
      onClick: () => {
        window.open(whitelabel.outlookURL, '"_new"');
      },
    },
    [INTEGRATION.ZOOM]: {
      component: Zoom,
    },
    [INTEGRATION.FACEBOOK]: {
      component: () => <IntegrationDetailsPage connectSocialProfile />,
    },
    [INTEGRATION.YOUTUBE]: {
      component: () => <IntegrationDetailsPage connectSocialProfile />,
    },
    [INTEGRATION.IOS]: {
      component: () => (
        <IntegrationDetailsPage
          actionType={ActionTypes.INSTALL}
          onClick={() => {
            window.open(IOS_URL, '"_new"');
          }}
        />
      ),
      onClick: () => {
        window.open(IOS_URL, '"_new"');
      },
    },
    [INTEGRATION.ANDROID]: {
      component: () => (
        <IntegrationDetailsPage
          actionType={ActionTypes.INSTALL}
          onClick={() => {
            window.open(ANDROID_INSTALL_LINK, '"_new"');
          }}
        />
      ),
      onClick: () => {
        window.open(ANDROID_INSTALL_LINK, '"_new"');
      },
    },
    [INTEGRATION.SALESFORCE]: {
      component: Salesforce,
    },
    [INTEGRATION.ELEMENT_451]: {
      component: () => <IntegrationDetailsPage />,
    },

    [INTEGRATION.CREDIT700]: {
      component: () => (
        <IntegrationDetailsPage actionType={ActionTypes.CONTACT} />
      ),
    },
    [INTEGRATION.DEALERSOCKET]: {
      component: () => (
        <IntegrationDetailsPage actionType={ActionTypes.CONTACT} />
      ),
    },
    [INTEGRATION.VIN_SULUTIONS]: {
      component: () => (
        <IntegrationDetailsPage actionType={ActionTypes.CONTACT} />
      ),
    },
    [INTEGRATION.CDK]: {
      component: () => (
        <IntegrationDetailsPage actionType={ActionTypes.CONTACT} />
      ),
    },
    [INTEGRATION.IPACKET]: {
      component: () => <IntegrationDetailsPage />,
    },
    [INTEGRATION.PROMAX]: {
      component: () => (
        <IntegrationDetailsPage actionType={ActionTypes.CONTACT} />
      ),
    },
    [INTEGRATION.TMS]: {
      component: () => (
        <IntegrationDetailsPage actionType={ActionTypes.CONTACT} />
      ),
    },
    [INTEGRATION.AUTOMINER]: {
      component: () => (
        <IntegrationDetailsPage actionType={ActionTypes.CONTACT} />
      ),
    },
    [INTEGRATION.EDEALER]: {
      component: () => (
        <IntegrationDetailsPage actionType={ActionTypes.CONTACT} />
      ),
    },
    [INTEGRATION.AUTO_SWEET]: {
      component: () => (
        <IntegrationDetailsPage actionType={ActionTypes.CONTACT} />
      ),
    },
    [INTEGRATION.PREDIAN]: {
      component: () => (
        <IntegrationDetailsPage actionType={ActionTypes.CONTACT} />
      ),
    },
    [INTEGRATION.DEALER_INSPIRE]: {
      component: () => (
        <IntegrationDetailsPage actionType={ActionTypes.CONTACT} />
      ),
    },
    [INTEGRATION.HOMENET]: {
      component: () => (
        <IntegrationDetailsPage actionType={ActionTypes.CONTACT} />
      ),
    },
    [INTEGRATION.VAUTO]: {
      component: () => (
        <IntegrationDetailsPage actionType={ActionTypes.CONTACT} />
      ),
    },
    [INTEGRATION.DEALERSLINK]: {
      component: () => (
        <IntegrationDetailsPage actionType={ActionTypes.CONTACT} />
      ),
    },
    [INTEGRATION.JD_POWER]: {
      component: () => (
        <IntegrationDetailsPage actionType={ActionTypes.CONTACT} />
      ),
    },
    [INTEGRATION.AUTO_VERIFY]: {
      component: () => (
        <IntegrationDetailsPage actionType={ActionTypes.CONTACT} />
      ),
    },
    [INTEGRATION.INVENTORY_PLUS]: {
      component: () => (
        <IntegrationDetailsPage actionType={ActionTypes.CONTACT} />
      ),
    },
    [INTEGRATION.ACV_MAX]: {
      component: () => (
        <IntegrationDetailsPage actionType={ActionTypes.CONTACT} />
      ),
    },
    [INTEGRATION.VIN_CUE]: {
      component: () => (
        <IntegrationDetailsPage actionType={ActionTypes.CONTACT} />
      ),
    },
    [INTEGRATION.AUTO_REVOLUTION]: {
      component: () => (
        <IntegrationDetailsPage actionType={ActionTypes.CONTACT} />
      ),
    },
    [INTEGRATION.DEALER_CENTER]: {
      component: () => (
        <IntegrationDetailsPage actionType={ActionTypes.CONTACT} />
      ),
    },
    [INTEGRATION.WHEELS_TV]: {
      component: () => (
        <IntegrationDetailsPage actionType={ActionTypes.CONTACT} />
      ),
    },
    [INTEGRATION.DEALER_CLOUD]: {
      component: () => (
        <IntegrationDetailsPage actionType={ActionTypes.CONTACT} />
      ),
    },
    [INTEGRATION.DEALER_COM]: {
      component: () => (
        <IntegrationDetailsPage actionType={ActionTypes.CONTACT} />
      ),
    },
    [INTEGRATION.LESA]: {
      component: () => (
        <IntegrationDetailsPage actionType={ActionTypes.CONTACT} />
      ),
    },
    [INTEGRATION.VINMOTION]: {
      component: () => (
        <IntegrationDetailsPage actionType={ActionTypes.CONTACT} />
      ),
    },
    [INTEGRATION.DEALER_CAR_SEARCH]: {
      component: () => (
        <IntegrationDetailsPage actionType={ActionTypes.CONTACT} />
      ),
    },
    [INTEGRATION.CARS_FOR_SALE]: {
      component: () => (
        <IntegrationDetailsPage actionType={ActionTypes.CONTACT} />
      ),
    },
    [INTEGRATION.MOMENTUM]: {
      component: () => (
        <IntegrationDetailsPage actionType={ActionTypes.CONTACT} />
      ),
    },
    [INTEGRATION.AUTOTRADER]: {
      component: () => (
        <IntegrationDetailsPage actionType={ActionTypes.CONTACT} />
      ),
    },
    [INTEGRATION.NETLOOK]: {
      component: () => (
        <IntegrationDetailsPage actionType={ActionTypes.CONTACT} />
      ),
    },
    [INTEGRATION.SINCRO]: {
      component: () => (
        <IntegrationDetailsPage actionType={ActionTypes.CONTACT} />
      ),
    },
    [INTEGRATION.DEALER_EPROCESS]: {
      component: () => (
        <IntegrationDetailsPage actionType={ActionTypes.CONTACT} />
      ),
    },
    [INTEGRATION.DEALPACK]: {
      component: () => (
        <IntegrationDetailsPage actionType={ActionTypes.CONTACT} />
      ),
    },
    [INTEGRATION.AGENCY_360]: {
      component: () => (
        <IntegrationDetailsPage actionType={ActionTypes.CONTACT} />
      ),
    },
    [INTEGRATION.ASTRAWEB]: {
      component: () => (
        <IntegrationDetailsPage actionType={ActionTypes.CONTACT} />
      ),
    },
    [INTEGRATION.CARPRAZE]: {
      component: () => (
        <IntegrationDetailsPage actionType={ActionTypes.CONTACT} />
      ),
    },
    [INTEGRATION.DEALERSYNC]: {
      component: () => (
        <IntegrationDetailsPage actionType={ActionTypes.CONTACT} />
      ),
    },
    [INTEGRATION.DEALERCUE]: {
      component: () => (
        <IntegrationDetailsPage actionType={ActionTypes.CONTACT} />
      ),
    },
    [INTEGRATION.PODIUM]: {
      component: () => (
        <IntegrationDetailsPage
          actionType={ActionTypes.CONNECT}
          isBtnDisabled={isGeneratingOAuthUrl || isDisconnectingPodium}
          btnText={!isPodiumConnected ? 'Connect' : 'Disconnect'}
          btnVariant={isPodiumConnected ? 'red' : 'primary'}
          onClick={handlePodiumButtonClick}
        />
      ),
      onClick: handlePodiumButtonClick,
    },
  };

  useEffect(() => {
    const processedIntegrations = integrationDetails?.map(
      (integration: Integration) => {
        let enabled = true;
        if (integration.name === INTEGRATION.SALESFORCE) {
          enabled = isSalesforceFeatureEnabled;
        }

        if (integration.name === INTEGRATION.ELEMENT_451) {
          enabled = isElementAccessible;
        }

        if (integration.name === INTEGRATION.DEALERSOCKET) {
          enabled = isDealerSocketAccessible;
        }

        const mappedData =
          IntegrationComponentMapping[
            integration.name as keyof typeof IntegrationComponentMapping
          ];

        const { component, imgSrc, onClick } = mappedData || {
          component: () => (
            <IntegrationDetailsPage actionType={ActionTypes.CONTACT} />
          ),
          imgSrc: '',
          onClick: undefined,
        };

        return {
          ...integration,
          key: integration.name.toLocaleLowerCase(),
          enabled,
          description: integration.description,
          i18nBaseKey: `${integration.key}-INTEGRATION`,
          path: `/integrations/${integration.name.toLowerCase()}`,
          component,
          imgSrc: integration.imgSrc || imgSrc,
          onClick,
        };
      }
    );

    setIntegrations(processedIntegrations);
  }, []);

  return {
    INTEGRATIONS: integrations,
    DISCOVER_BY_FILTERS,
    CDS_CATEGORIES,
    INTEGRATIONS_CATEGORIES,
  };
};

export const useIntegrationTypeFilter = () => {
  const { INTEGRATIONS } = useIntegrationData();
  const filter = (showTypes: INTEGRATION_TYPE[]) => {
    return INTEGRATIONS.filter(integration => {
      // Check if at least one integration type in showTypes array matches the current integration's show property
      return showTypes.some(type => integration.show.includes(type));
    });
  };
  return filter;
};
